"use client";
import Image from "next/image";
import React, { useRef, useState } from "react";
import { zapierWebhookCairaAuditors } from "@/lib/utils";
// import { useEvent } from "@/contexts/EventContext";
import {
  useWebinarSchedule,
  useWebinarScheduleLoading,
} from "@/contexts/EventContext";
import { getWebinarEntry, getOrdinalDateParts } from "@/data/webinarSchedule";
import WebinarDateSkeleton from "@/components/common/WebinarDateSkeleton";
// import { motion } from "framer-motion";

type CairaAuditorsFormVariant = "webinar" | "roadmap";

type CairaAuditorsFormProps = {
  submitButton?: string;
  darkBackground?: boolean;
  /** Preset heading / subheading / button copy. Defaults to "webinar". */
  variant?: CairaAuditorsFormVariant;
  /** Optional overrides for the preset copy. */
  heading?: string;
  subheading?: string;
  /** Hide the form's built-in heading/subheading (e.g. when the parent renders its own). */
  showHeading?: boolean;
};

const COUNTRY_CODE = "+91"; // IN – India

const CairaAuditorsForm: React.FC<CairaAuditorsFormProps> = ({
  darkBackground = false,
  showHeading = true,
}) => {
  // const latestEvent = useEvent("CAIRA");
  // const formattedDate = latestEvent?.formattedDate
  //   ? latestEvent.formattedDate.split(" ").slice(0, 2).join(" ")
  //   : "";
  // const time = latestEvent?.time ? latestEvent?.time : "4:00 PM";
  // const day = latestEvent?.day ? latestEvent?.day : "Saturday";

  const webinarSchedule = useWebinarSchedule();
  const isScheduleLoading = useWebinarScheduleLoading();
  const weeklyWebinarEntry = getWebinarEntry("weekly", webinarSchedule);
  const weeklyDateParts = getOrdinalDateParts(weeklyWebinarEntry.date);

  const initialState = {
    name: "",
    email: "",
    phone: "",
  };

  const [formFields, setFormFields] = useState(initialState);
  const [phoneError, setPhoneError] = useState<string>("");
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [connectWhatsApp, setConnectWhatsApp] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const formRef = useRef<HTMLFormElement>(null);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow digits only
    const digits = e.target.value.replace(/\D/g, "");
    setFormFields((prev) => ({ ...prev, phone: digits }));
    if (phoneError) setPhoneError("");
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConnectWhatsApp(e.target.checked);
  };

  const validatePhone = (): boolean => {
    if (!formFields.phone) {
      setPhoneError("Phone number is required.");
      return false;
    }
    // IN local numbers are 10 digits
    if (formFields.phone.length !== 10) {
      setPhoneError("Enter a valid 10-digit phone number.");
      return false;
    }
    setPhoneError("");
    return true;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validatePhone()) return;

    const formData = new FormData(e.currentTarget);
    const data = Object.fromEntries(formData);

    const payload = {
      name: data.name as string,
      email: data.email as string,
      phone: `${COUNTRY_CODE}${formFields.phone}`,
      pageUrl: typeof window !== "undefined" ? window.location.href : "",
      timestamp: new Date().toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        hour12: false,
      }),
    };

    setIsLoading(true);
    try {
      const { error } = await zapierWebhookCairaAuditors(payload);

      if (!error) {
        (window as any).gtag?.("event", "conversion", {
          send_to: "AW-16816962332/VPOcCLKM6rMcEJz2-dI-",
        });
        setCurrentStep(2);
        formRef.current?.reset();
        setFormFields(initialState);
        setConnectWhatsApp(true);
      } else {
        setCurrentStep(1);
        console.error("Zapier Webhook Error:", error);
      }
    } catch (error) {
      console.error("Network Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const inputBase = darkBackground
    ? "text-white bg-black border-black placeholder:text-[#7C828A]"
    : "text-white bg-black border-black placeholder:text-[#7C828A]";

  return (
    <div>
      {currentStep === 1 && (
        <form ref={formRef} onSubmit={handleSubmit} className="flex flex-col">
          {/* Heading + Subheading */}
          {showHeading && (
            <div className="text-center pb-4 mb-2 w-[330px] mx-auto">
              {/* <h3 className="text-white text-lg font-semibold">
                {latestEvent?.title?.replace(/-/g, " ")}
              </h3> */}
              <h3 className="text-white text-lg font-semibold">
                Build your <span className="text-[#FFBA08]">1st AI Agent</span>{" "}
                in 2 hours
              </h3>
              <p className="text-sm font-semibold leading-normal mt-2 text-[#fff]">
                <span className="inline-block w-2 h-2 bg-[#f33] rounded-full mr-1.5"></span>
                <span className="text-[#f33]">Live:</span>{" "}
                {isScheduleLoading ? (
                  <WebinarDateSkeleton width={180} />
                ) : (
                  <>
                    {weeklyDateParts.day}
                    <sup>{weeklyDateParts.suffix}</sup>{" "}
                    {weeklyDateParts.month.slice(0, 3)},{" "}
                    {weeklyWebinarEntry.day} at{" "}
                    <span className="text-[#FFBA08]">
                      {weeklyWebinarEntry.time}
                    </span>
                  </>
                )}
              </p>
              {/* <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{
                  opacity: formattedDate && day ? 1 : 0,
                  y: formattedDate && day ? 0 : 10,
                }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="text-sm mt-1 text-[#B1B1B1]"
              >
                {formattedDate}, {day} at{" "}
                <motion.span
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{
                    opacity: time ? 1 : 0,
                    scale: time ? 1 : 0.9,
                  }}
                  transition={{
                    duration: 0.1,
                    delay: 0.8,
                    ease: "easeOut",
                  }}
                  className="wbFormTime"
                >
                  {time}
                </motion.span>
              </motion.p> */}
            </div>
          )}

          {/* Name */}
          <div className="mb-2">
            <label htmlFor="name" className="sr-only">
              Full Name
            </label>
            <input
              id="name"
              type="text"
              name="name"
              placeholder="Full Name*"
              required
              className={`appearance-none block !text-sm md:text-base w-full md:h-[42px] lg:h-[42px] font-medium h-[42px] rounded-xl border py-3 px-4 leading-tight focus-visible:outline-none text-[#FFF] bg-[#000] border-[#333] border-[1px] placeholder:text-[#777] ${inputBase}`}
            />
          </div>

          {/* Phone – fixed IN country code */}
          <div className="mb-2">
            <div
              className={`flex items-center w-full h-[42px] border-[#333] border-[1px] placeholder:text-[#777] rounded-xl overflow-hidden bg-black ${
                phoneError ? "border-red-500" : "border-black"
              }`}
            >
              {/* Fixed prefix with dropdown chevron (India only) */}
              <span className="flex-shrink-0 flex items-center gap-1.5 pl-4 pr-2 text-sm md:text-base font-medium text-white">
                {COUNTRY_CODE}
                <svg
                  className="text-[#7C828A]"
                  width="14"
                  height="14"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M3 4.5L6 7.5L9 4.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>

              {/* Local number */}
              <label htmlFor="phone" className="sr-only">
                Phone Number
              </label>
              <input
                id="phone"
                type="tel"
                name="phone"
                value={formFields.phone}
                onChange={handlePhoneChange}
                placeholder="Phone Number*"
                maxLength={10}
                inputMode="numeric"
                className="flex-1 h-full !text-sm md:text-base font-medium py-3 px-3 leading-tight focus-visible:outline-none appearance-none text-white bg-black placeholder:text-[#7C828A]"
              />
            </div>

            {phoneError && (
              <p className="mt-1 text-xs text-red-500">{phoneError}</p>
            )}
          </div>

          {/* Email */}
          <div className="mb-2">
            <label htmlFor="email" className="sr-only">
              Email Address
            </label>
            <input
              id="email"
              type="email"
              name="email"
              placeholder="E-mail*"
              required
              className={`appearance-none block !text-sm md:text-base w-full md:h-[42px] lg:h-[42px] font-medium h-[42px] rounded-xl border py-3 px-4 leading-tight focus-visible:outline-none text-[#FFF] bg-[#000] border-[#333] border-[1px] placeholder:text-[#777] ${inputBase}`}
            />
          </div>

          {/* WhatsApp consent */}
          <div className="mb-3 ml-1 flex items-center justify-start">
            <input
              id="page"
              name="connectWhatsApp"
              type="checkbox"
              checked={connectWhatsApp}
              onChange={handleCheckboxChange}
              className={`relative w-5 h-5 md:min-w-[20px] appearance-none cursor-pointer
                            rounded-full mr-2 border-2 ${darkBackground ? "border-white" : "border-white"}
                            checked:bg-[#39f] checked:border-0
                            after:content-['✓'] after:absolute after:top-1/2 after:left-1/2
                            after:-translate-x-1/2 after:-translate-y-1/2 after:text-[10px]
                            after:font-bold after:text-white after:opacity-0
                            checked:after:opacity-100 after:transition-opacity after:duration-200`}
            />

            <label
              htmlFor="page"
              className={`text-[8px] md:text-[9px] font-medium tracking-[-0.373px] cursor-pointer ${
                darkBackground ? "text-white" : "text-white"
              }`}
            >
              I agree to receive updates via
              <Image
                src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit-new/whatsapp_icon.webp"
                alt="Whatsapp logo"
                width={12}
                height={12}
                className="mx-[2px] inline-block"
              />
              WhatsApp, Email & SMS.
              <a
                href="/privacy"
                target="_blank"
                className={`ml-1 underline ${
                  darkBackground ? "text-[#FFBA08]" : "text-[#FFBA08]"
                }`}
              >
                Privacy Policy
              </a>
            </label>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            aria-label="Register Now"
            className="font-semibold bg-[#3399FF] text-white text-xs md:text-sm py-2 w-full capitalize rounded-[24px_24px_24px_0px] disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <svg
                  className="animate-spin h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                Submitting...
              </>
            ) : (
              "Register Now"
            )}
          </button>
        </form>
      )}

      {currentStep === 2 && (
        <div>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-16 w-16 mx-auto mb-6 text-green-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 12l2 2 4-4m6 2a9 9 0 11-9-9 9 9z"
            />
          </svg>
          <h3 className="text-[#fff] md:text-2xl text-xl font-bold text-center mb-2">
            Thank you for your ​i​nquiry.
          </h3>
          <h2 className="text-[#fff] md:text-xl text-lg font-normal text-center">
            Our programme advisor will get back to you shortly!
          </h2>
        </div>
      )}
    </div>
  );
};

export default CairaAuditorsForm;
